'use client'
import Button from '@/components/ui/Button/Button'
import ProductBuyBox from './ProductBuyBox'
import ProductGallery from './ProductGallery'
import { Tabs } from '@/components/ui'
import Container from '../../../components/CatalogContainer'
import { ProductCardSlider } from '@/components/template/ProductCardSlider'
import Link from 'next/link'
import { FaArrowRight } from 'react-icons/fa'
import Breadcrumbs from '@/components/ui/Breadcrumbs'
import type { Image } from './type'
import { buildBlocks } from './parseDescription'
interface ProductCombProps {
    productInfo: {
        id: string
        name: string
        description: string
        images: Image[] | string[]
        local_main_image?: string | undefined
        variants: {
            id: string
            vendorCode: string
            color: string | null
            size: string | null
            price: number
            available: boolean
            currency: string
        }[]
        gender: string
        season: string
        section: string
        features: string
        materials: string
    }
    withTogether: any
    categoryName: any
}

export const ProductComb = ({
    productInfo,
    withTogether,
    categoryName,
}: ProductCombProps) => {
    const mainVariant = productInfo.variants?.[0] ?? null

    const variants = productInfo.variants ?? []

    // уникальные цвета
    const colors = Array.from(
        new Set(variants.map((v) => v.color).filter(Boolean)),
    )

    // уникальные размеры
    const sizes = Array.from(
        new Set(variants.map((v) => v.size).filter(Boolean)),
    )
    const galleryImages: string[] = Array.isArray(productInfo.images)
        ? productInfo.images.map((img) =>
              typeof img === 'string' ? img : img.local_src,
          )
        : []
    const blocks = buildBlocks(productInfo.description)
    return (
        <Container>
            <Breadcrumbs categoriesbc={categoryName} />
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                <div className="min-w-0 flex-1">
                    <ProductGallery
                        images={galleryImages}
                        main_image={productInfo.local_main_image}
                    />
                </div>

                <div className="w-full lg:w-[420px] flex-shrink-0">
                    <ProductBuyBox
                        product={{
                            id: mainVariant?.id,
                            sku: mainVariant?.vendorCode ?? '',
                            name: productInfo.name,
                            price: mainVariant?.price ?? 0,
                            priceCurrency: mainVariant?.currency ?? '₽',
                            oldPrice: null,
                            rating: 0,
                            reviewsCount: 0,
                            availableCount: mainVariant?.available ? 10 : 0,
                            location: 'Москва и МО',
                            deliveryDate: undefined,
                            pickupDate: undefined,
                            pickupPriceText: undefined,
                            deliveryPriceText: undefined,
                            isPickupAvailable: undefined,
                            isInStock: mainVariant?.available ?? false,
                            variants: variants,
                            color: colors,
                            sizes: sizes,
                        }}
                    />
                </div>
            </div>

            <div className="hidden lg:block">
                <Tabs defaultValue="description" variant="underline">
                    <Tabs.TabList className="flex gap-6 border-b border-gray-200">
                        <Tabs.TabNav value="description">Описание</Tabs.TabNav>
                        <Tabs.TabNav value="specs">Характеристики</Tabs.TabNav>
                        <Tabs.TabNav value="together">
                            Покупают вместе
                        </Tabs.TabNav>
                        {/* <Tabs.TabNav value="reviews">Отзывы</Tabs.TabNav> */}
                    </Tabs.TabList>

                    <Tabs.TabContent value="description" className="py-6">
                        <div className="my-4 uppercase text-[21px] font-semibold leading-6.5">
                            Описание
                        </div>
                        <p className="font-normal text-[16px] leading-6 text-dark-color whitespace-pre-line">
                            {blocks.map((block, index) => {
                                if (block.type === 'title') {
                                    return (
                                        <h3
                                            key={index}
                                            className="font-semibold text-[16px] py-1.5"
                                        >
                                            {block.text}
                                        </h3>
                                    )
                                }

                                if (block.type === 'list') {
                                    return (
                                        <ul
                                            key={index}
                                            className="list-disc pl-5 space-y-2"
                                        >
                                            {block.items.map((item, i) => (
                                                <li key={i}>{item}</li>
                                            ))}
                                        </ul>
                                    )
                                }

                                return <p key={index}>{block.text}</p>
                            })}
                        </p>
                    </Tabs.TabContent>

                    <Tabs.TabContent value="specs" className="py-6">
                        <h2 className="my-[16px] uppercase text-[21px] font-semibold leading-[26px]">
                            Характеристики
                        </h2>

                        <div className="flex flex-col gap-2 text-[16px] leading-[24px] text-dark-color">
                            <div>
                                <strong>Пол:</strong>{' '}
                                {productInfo.gender ?? '—'}
                            </div>
                            <div>
                                <strong>Сезон:</strong>{' '}
                                {productInfo.season ?? '—'}
                            </div>
                            <div>
                                <strong>Раздел:</strong>{' '}
                                {productInfo.section ?? '—'}
                            </div>
                            <div>
                                <strong>Особенности:</strong>
                                {productInfo.features
                                    ? productInfo.features
                                          .split(/ (?=[А-ЯЁ][а-яё])/g)
                                          .map((item) => item.trim())
                                          .filter(Boolean)
                                          .map((feature, i) => (
                                              <div
                                                  key={i}
                                                  className="flex gap-2"
                                              >
                                                  <span className="text-[#FFB240]">
                                                      •
                                                  </span>
                                                  <span>{feature}</span>
                                              </div>
                                          ))
                                    : 'Нет данных'}
                            </div>
                            <div>
                                <strong>Материалы:</strong>{' '}
                                {productInfo.materials ?? '—'}
                            </div>
                        </div>
                    </Tabs.TabContent>

                    <Tabs.TabContent value="together" className="py-6">
                        <div className="my-[16px] uppercase text-[21px] font-semibold leading-[26px]">
                            Покупают вместе
                        </div>

                        <div className="flex flex-col">
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                                {withTogether.map((el: any) => {
                                    return (
                                        <ProductCardSlider
                                            className="flex flex-col"
                                            key={el.product_id}
                                            {...el}
                                        />
                                    )
                                })}
                            </div>
                            <div className="mt-20 text-center">
                                <Link
                                    href="/catalog/all"
                                    className="button button bg-white border border-gray-300 dark:bg-gray-700 dark:border-gray-700 dark:ring-white dark:hover:border-white hover:ring-1 dark:hover:text-white dark:hover:bg-transparent text-gray-600 dark:text-gray-100 tracking-[0.2px] text-[17px] font-semibold leading-[24px] h-12 rounded-xl px-5 py-2 inline-flex gap-4 items-center button-press-feedback"
                                >
                                    Смотреть все товары
                                    <FaArrowRight size={16} />
                                </Link>
                            </div>
                        </div>
                    </Tabs.TabContent>

                    {/* <Tabs.TabContent value="reviews" className="py-6">
                        <h2 className="my-[16px] uppercase text-[21px] font-semibold leading-[26px]">
                            Отзывы
                        </h2>

                        <p className="font-normal text-[16px] leading-[24px] text-dark-color mb-[28px]">
                            Отзывов пока нет. Будем рады, если вы оставите
                            первый!
                        </p>

                        <Button className="bg-gray-900 text-white py-3 rounded-lg text-[17px] font-semibold leading-[24px] hover:opacity-95">
                            Написать отзыв
                        </Button>
                    </Tabs.TabContent> */}
                </Tabs>
            </div>

            <div className="block lg:hidden px-2">
                <section id="description" className="py-6 scroll-mt-24">
                    <h2 className="my-[16px] uppercase racking-[0.25px] text-[21px] font-semibold leading-[26px]">
                        Описание
                    </h2>
                    <p className="font-normal text-[16px] leading-[24px] text-dark-color">
                        {productInfo.description}
                    </p>
                </section>

                <section id="specs" className="scroll-mt-24">
                    <h2 className="my-[16px] uppercase racking-[0.25px] text-[21px] font-semibold leading-[26px]">
                        Характеристики
                    </h2>
                    <div className="font-normal text-[16px] leading-[24px] text-dark-color space-y-3">
                        <h3 className="text-[18px] font-semibold pb-4">
                            Основное
                        </h3>

                        <ul className="flex flex-col gap-3 list-none">
                            <li>
                                <strong>Пол:</strong> {productInfo.gender}
                            </li>
                            <li>
                                <strong>Сезон:</strong> {productInfo.season}
                            </li>
                            <li>
                                <strong>Раздел:</strong>
                                {productInfo.section}
                            </li>
                            <li>
                                <strong>Особенности:</strong>{' '}
                                {(productInfo.features ?? '')
                                    .split(/ (?=[А-ЯЁ][а-яё])/g)
                                    .map((item) => item.trim())
                                    .filter(Boolean)
                                    .map((feature, i) => (
                                        <div key={i} className="flex gap-2">
                                            <span className="text-[#FFB240]">
                                                •
                                            </span>
                                            <span>{feature}</span>
                                        </div>
                                    ))}
                            </li>
                            <li>
                                <strong>Материалы:</strong>{' '}
                                {productInfo.materials}
                            </li>
                        </ul>
                    </div>
                </section>

                <section id="together" className="py-6 scroll-mt-24">
                    <h2 className="my-[16px] uppercase racking-[0.25px] text-[21px] font-semibold leading-[26px]">
                        Покупают вместе
                    </h2>
                    <div className="flex flex-col gap-3">
                        {withTogether.map((el) => {
                            return (
                                <Link
                                    className="hover:underline"
                                    key={el.product_id}
                                    href={`/catalog/product/${el.latin}`}
                                >
                                    <div className="flex gap-2">
                                        <div className="w-[25px] h-[25px]">
                                            <img
                                                src={`/img/${el.local_main_image}`}
                                                alt={el.id}
                                            />
                                        </div>
                                        <span> {el.name}</span>
                                    </div>
                                </Link>
                            )
                        })}
                        <div className="mt-2">
                            <Link
                                href="/catalog/all"
                                className="button bg-white border border-gray-300 dark:bg-gray-700 dark:border-gray-700 dark:ring-white dark:hover:border-white hover:ring-1 dark:hover:text-white dark:hover:bg-transparent text-gray-600 dark:text-gray-100 tracking-[0.2px] text-sm font-semibold  h-12 rounded-lg px-4 py-2 inline-flex gap-4 items-center button-press-feedback"
                            >
                                Смотреть все товары
                                <FaArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* <section id="reviews" className="pt-4 scroll-mt-24 ">
                    <h2 className="my-[16px] uppercase racking-[0.25px] text-[21px] font-semibold leading-[26px]">
                        Отзывы
                    </h2>
                    <p className="font-normal text-[16px] leading-[24px] text-dark-color mb-[28px]">
                        Отзывов пока нет. Будем рады, если вы оставите первый!
                    </p>
                    <Button className="mb-[28px] bg-gray-900 text-white py-3 rounded-lg tracking-[0.2px] text-sm font-semibold leading-[24px] hover:opacity-95 hover:text-white transition-shadow shadow-sm disabled:bg-gray-400">
                        Написать отзыв
                    </Button>
                </section> */}
            </div>
        </Container>
    )
}
